import { useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import Skeleton from "../../components/Skeleton";
import { useSnackbar } from "../../components/Snackbar";
import Spinner from "../../components/Spinner";
import useEnableCapitalMutation from "../../hooks/data/mutations/useEnableCapitalMutation";
import { capitalize, snakeCaseToNormal } from "../../utils/string";
import Panel from "./Panel";
import HasAccess, { csrAdminRoles } from "../../components/HasAccess";

type Props = {
  accountHolderCode: string;
  offerStatus: string | null;
  grantStatus: string | null;
  isLoading: boolean;
};

function CapitalInfo({
  accountHolderCode,
  offerStatus,
  grantStatus,
  isLoading,
}: Props): JSX.Element {
  const { mutate: enableCapital, isLoading: isEnablingCapital } =
    useEnableCapitalMutation();
  const { openSnackbar } = useSnackbar({
    duration: 5000,
    position: "top",
  });
  const navigate = useNavigate();

  const showEnableCapital =
    (offerStatus === null || offerStatus === "") &&
    (grantStatus === null || offerStatus === "");

  async function handleEnableCapitalClick() {
    enableCapital(accountHolderCode, {
      onSuccess() {
        openSnackbar(`Capital enabled for ${accountHolderCode}.`, {
          type: "success",
        });
      },
      onError(e) {
        openSnackbar(e.message, {
          type: "error",
        });
      },
    });
  }

  function handleGoToCapitalClick() {
    navigate(`/capital/${accountHolderCode}`);
  }

  return (
    <Panel>
      <Panel.Header title="Capital">
        <HasAccess allowedRoles={[...csrAdminRoles]}>
          {showEnableCapital ? (
            <Button
              size="sm"
              onClick={handleEnableCapitalClick}
              icon={isEnablingCapital && <Spinner />}
              disabled={isEnablingCapital}
            >
              Enable Capital
            </Button>
          ) : (
            <Button size="sm" onClick={handleGoToCapitalClick}>
              Go To Capital
            </Button>
          )}
        </HasAccess>
      </Panel.Header>
      <Panel.Content>
        <div className="grid grid-cols-2 grid-rows-2 gap-x-2 gap-y-2">
          <div className="font-medium">Offers</div>
          <div className="truncate">
            {isLoading ? (
              <Skeleton />
            ) : (
              <div>
                {!offerStatus
                  ? "N/A"
                  : capitalize(snakeCaseToNormal(offerStatus))}
              </div>
            )}
          </div>
          <div className="font-medium">Grants</div>
          <div className="truncate">
            {isLoading ? (
              <Skeleton />
            ) : (
              <div>
                {!grantStatus
                  ? "N/A"
                  : capitalize(snakeCaseToNormal(grantStatus))}
              </div>
            )}
          </div>
        </div>
      </Panel.Content>
    </Panel>
  );
}

export default CapitalInfo;
